import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Play, ExternalLink } from 'lucide-react';

/**
 * AlphaVideoModal
 * - Renders a launcher button + a modal that plays the full Alpha Film Series playlist
 *   starting at Episode 1. The YouTube iframe is only mounted while the dialog is open
 *   so closing it instantly stops playback.
 */
export const AlphaVideoModal = ({
  buttonLabel = 'Start Alpha at Episode 1',
  buttonVariant = 'default',
  buttonClassName = '',
  fullWidth = false,
  size = 'default',
  testId = 'start-alpha-button',
}) => {
  const [open, setOpen] = React.useState(false);

  // YouTube playlist that starts on Ep 1 with autoplay when the modal opens.
  const PLAYLIST_ID = 'PLA7xqrtB-HHrET4NrEPUmVhVorhQpKGRr';
  // Use the videoseries embed so YouTube serves the first available video in the playlist.
  // (Hardcoding a specific video ID risks "video unavailable" if the playlist's lead video changes.)
  const embedSrc = `https://www.youtube.com/embed/videoseries?list=${PLAYLIST_ID}&autoplay=1&rel=0`;
  const playlistUrl = `https://www.youtube.com/playlist?list=${PLAYLIST_ID}`;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size={size}
        variant={buttonVariant}
        className={`${fullWidth ? 'w-full' : ''} ${
          buttonVariant === 'default'
            ? 'bg-red-600 hover:bg-red-700 text-white'
            : ''
        } ${buttonClassName}`}
        data-testid={testId}
      >
        <Play className="w-4 h-4 mr-2 fill-current" />
        {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-4xl p-0 overflow-hidden"
          data-testid="alpha-video-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200">
            <DialogTitle className="text-2xl font-heading text-red-700 flex items-center gap-2">
              <Play className="w-6 h-6 fill-current" />
              Alpha Film Series — Episode 1
            </DialogTitle>
            <DialogDescription>
              Is There More To Life Than This? Watch the full 14-episode journey — episodes
              auto-advance through the playlist.
            </DialogDescription>
          </DialogHeader>

          {open && (
            <div className="aspect-video w-full bg-black">
              <iframe
                src={embedSrc}
                title="Alpha Film Series"
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="alpha-video-iframe"
              />
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-50 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              14 episodes • exploring the basics of the Christian faith
            </p>
            <a
              href={playlistUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:text-red-800"
            >
              View full playlist on YouTube
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AlphaVideoModal;
